import { Table } from 'Components/Layout';
import React from 'react';
import { FormatTypes } from '../../Lib/Format';
import { Field, TextOutput } from '../Core';
import {
  SHAPE_CIRCULAR as SIEVE_SET_SHAPE_CIRCULAR,
  SHAPE_RECTANGULAR as SIEVE_SET_SHAPE_RECTANGULAR,
  UNITS_METRIC,
} from './SieveSet/SieveSetHelper';

interface IEquipmentType {
  name: string;
  calibration?: (arg: any) => React.ReactNode[];
  additionalDataFields?: (arg: any) => React.ReactNode[];
  endpoint?: string;
  isInterfacingSupported?: boolean;
}

interface IEquipmentTypes {
  [type: string]: IEquipmentType;
}

const EquipmentTypes: IEquipmentTypes = {
  balance: {
    name: 'Balance',
    isInterfacingSupported: true,
    additionalDataFields: ({ Uom: uom, Readability: readability }: any) => [
      <Field label="Units" input={<TextOutput />} value={uom} key={0} horizontal />,
      <Field label="Readable to Nearest" input={<TextOutput />} value={readability} key={1} horizontal />,
    ],
  },
  'bitumen-centrifuge-extractor': {
    name: 'Bitumen Centrifuge Extractor',
  },
  centrifuge: {
    name: 'Centrifuge',
  },
  'compression-machine': {
    name: 'Compression Machine',
    isInterfacingSupported: true,
  },
  'cylinder-jack': {
    name: 'Cylinder Jack',
    calibration: ({ Mass: { value: massVal = undefined, uom: massUom = undefined } = {} }) => [
      <Field
        label={'Mass' + (massUom ? ` (${massUom})` : '')}
        input={<TextOutput />}
        value={massVal}
        format={{ nearest: 0.001 }}
        key={0}
        horizontal
      />,
    ],
  },
  'deflection-gauge': {
    name: 'Deflection Gauge',
    calibration: ({ Factor: { value: factorVal = undefined, uom: factorUom = undefined } = {} }) => [
      <Field
        label={'Gauge Factor (mm/div)'}
        input={<TextOutput />}
        value={factorVal}
        format={{ nearest: 0.01 }}
        key={0}
        horizontal
      />,
    ],
    additionalDataFields: ({ Readability: readability }: any) => [
      <Field label="Readable to Nearest" input={<TextOutput />} value={readability} key={1} horizontal />,
    ],
  },
  'density-mould': {
    name: 'Density Mold',
    calibration: ({
      Mass: { value: massVal = undefined, uom: massUom = undefined } = {},
      Volume: { value: volumeVal = undefined, uom: volumeUom = undefined } = {},
    }) => [
      <Field
        label={'Mass' + (massUom ? ` (${massUom})` : '')}
        input={<TextOutput />}
        value={massVal}
        format={{ precision: 4, type: FormatTypes.SignificantFigures }}
        key={0}
        horizontal
      />,
      <Field
        label={'Volume' + (volumeUom ? ` (${volumeUom})` : '')}
        input={<TextOutput />}
        value={volumeVal}
        format={{ precision: 4, type: FormatTypes.SignificantFigures }}
        key={1}
        horizontal
      />,
    ],
  },
  furnace: {
    name: 'Furnace',
  },
  'dynamic-cone-penetrometer': {
    name: 'Dynamic Cone Penetrometer',
  },
  caliper: {
    name: 'Calliper',
  },
  measure: {
    name: 'Measure',
  },
  'straight-edge': {
    name: 'Straight Edge',
  },
  'hand-shear-vane': {
    name: 'Hand Shear Vane',
    calibration: ({ MaximumReading: maxReading, Calibration19mm: Factor19mm, Calibration33mm: Factor33mm }) => [
      <Field label="Maximum Reading (divisions)" input={<TextOutput />} value={maxReading} key={0} horizontal />,
      <Field
        label="19 mm Blade Factor (kPa/div)"
        input={<TextOutput />}
        value={Factor19mm?.value}
        format={{ nearest: 0.0001 }}
        key={1}
        horizontal
      />,
      <Field
        label="33 mm Blade Factor (kPa/div)"
        input={<TextOutput />}
        value={Factor33mm?.value}
        format={{ nearest: 0.0001 }}
        key={2}
        horizontal
      />,
    ],
  },
  'sand-cone-pouring-apparatus': {
    name: 'Sand Cone Pouring Apparatus',
    calibration: ({
      Description: description,
      SandConeVolume: volume,
      InternalDiameter: internalDiameter,
      MassOfSand: massOfSand,
      MassOfSandInCone: massOfSandInCone,
    }) => [
      <Field label="Description" input={<TextOutput />} value={description} key={0} horizontal />,
      <Field
        label={'Volume' + (!!volume?.uom ? ` (${volume?.uom})` : '')}
        input={<TextOutput />}
        value={volume?.value}
        format={{ nearest: 0.0001 }}
        key={1}
        horizontal
      />,
      <Field
        label={'Internal Diameter' + (!!internalDiameter?.uom ? ` (${internalDiameter?.uom})` : '')}
        input={<TextOutput />}
        value={internalDiameter?.value}
        format={{ nearest: 0.1 }}
        key={2}
        horizontal
      />,
      <Field
        label={'Mass of Sand' + (!!massOfSand?.uom ? ` (${massOfSand?.uom})` : '')}
        input={<TextOutput />}
        value={massOfSand?.value}
        format={{ nearest: 1 }}
        key={3}
        horizontal
      />,
      <Field
        label={'Mass of Sand in Cone' + (!!massOfSandInCone?.uom ? ` (${massOfSandInCone?.uom})` : '')}
        input={<TextOutput />}
        value={massOfSandInCone?.value}
        format={{ nearest: 1 }}
        key={4}
        horizontal
      />,
    ],
  },
  'sand-bulk-density': {
    name: 'Sand Bulk Density',
    calibration: ({
      Units: units,
      BulkDensity: bulkDensity,
      CalibrationMethod: calibrationMethod,
      MaterialSource: materialSource,
      MaterialDescription: materialDescription,
    }) => [
      <Field label="Units" input={<TextOutput />} value={units} key={0} horizontal />,
      <Field
        label={'Bulk Density' + (!!bulkDensity?.uom ? ` (${bulkDensity?.uom})` : '')}
        input={<TextOutput />}
        value={bulkDensity?.value}
        format={{ nearest: 0.001 }}
        key={1}
        horizontal
      />,
      <Field label="Calibration Method" input={<TextOutput />} value={calibrationMethod} key={2} horizontal />,
      <Field label="Material Source" input={<TextOutput />} value={materialSource} key={3} horizontal />,
      <Field label="Material Description" input={<TextOutput />} value={materialDescription} key={4} horizontal />,
    ],
  },
  hammer: {
    name: 'Hammer',
    additionalDataFields: ({
      Operation: operation,
      FaceType: faceType,
      NominalUnits: units,
      FaceDiameter: diameter,
      FaceRadius: radius,
    }: any) => [
      <Field label="Operation" input={<TextOutput />} value={operation} key={0} horizontal />,
      <Field
        label="Face Type"
        input={<TextOutput />}
        value={
          [
            { label: 'Circular', value: 'circular' },
            { label: 'Sector (Pie)', value: 'sector' },
          ].find((o) => o.value === faceType)?.label
        }
        key={1}
        horizontal
      />,
      <Field
        label={'Face Nominal Diameter' + (!!diameter?.uom ? ` (${diameter?.uom})` : '')}
        input={<TextOutput />}
        value={diameter?.value}
        format={units === UNITS_METRIC ? { nearest: 0.01 } : { nearest: 0.001 }}
        visible={faceType === 'circular'}
        key={3}
        horizontal
      />,
      <Field
        label={'Face Nominal Radius' + (!!radius?.uom ? ` (${radius?.uom})` : '')}
        input={<TextOutput />}
        value={radius?.value}
        format={units === UNITS_METRIC ? { nearest: 0.01 } : { nearest: 0.001 }}
        visible={faceType === 'sector'}
        key={4}
        horizontal
      />,
    ],
  },
  hydrometer: {
    name: 'Hydrometer',
    calibration: ({ MeniscusCorrection: meniscusCorrection, Intercept: intercept, Slope: slope }) => [
      <Field
        label="Meniscus Correction (g/mL)"
        // TODO: remove hardcoded unit above, more like the below but need to replace the default uom with a custom one
        //label={'Meniscus Correction' + (!!meniscusCorrection?.uom ? ` (${meniscusCorrection?.uom})` : '')}
        input={<TextOutput />}
        value={meniscusCorrection?.value}
        format={{ nearest: 0.1 }}
        key={0}
        horizontal
      />,
      <Field
        label="Intercept"
        input={<TextOutput />}
        value={intercept}
        format={{ nearest: 0.01 }}
        key={1}
        horizontal
      />,
      <Field label="Slope" input={<TextOutput />} value={slope} format={{ nearest: 0.01 }} key={2} horizontal />,
    ],
  },
  'grooving-tool': {
    name: 'Grooving Tool',
    additionalDataFields: (value: any) => [
      <Field label="Grooving Tool Material" input={<TextOutput />} value={value.Type} key={0} horizontal />,
      <Field label="Grooving Tool Shape" input={<TextOutput />} value={value.Shape} key={1} horizontal />,
    ],
  },
  'load-cell': {
    name: 'Load Cell',
    calibration: ({ LoadCellFactor: { value: factorVal = undefined, uom: factorUom = undefined } = {} }) => [
      <Field
        label={'Load Cell Factor (kN/div)'}
        input={<TextOutput />}
        value={factorVal}
        format={{ nearest: 0.01 }}
        key={0}
        horizontal
      />,
    ],
    additionalDataFields: ({ Readability: readability }: any) => [
      <Field label="Readable to Nearest" input={<TextOutput />} value={readability} key={1} horizontal />,
    ],
  },
  'nuclear-density-gauge': {
    name: 'Nuclear Density Gauge',
    calibration: (calibrationValues) => [
      <Field label="Model Number" input={<TextOutput />} value={calibrationValues.ModelNumber} key={0} horizontal />,
    ],
    additionalDataFields: (value: any) => [
      <Field label="Serial Number" input={<TextOutput />} value={value.SerialNumber} key={1} horizontal />,
      <Field label="Make/Model" input={<TextOutput />} value={value.MakeAndModel} key={2} horizontal />,
    ],
  },
  oven: {
    name: 'Oven',
  },
  'shrinkage-mould': {
    name: 'Shrinkage Mould',
  },
  'liquid-limit-apparatus': {
    name: 'Liquid Limit Apparatus',
    additionalDataFields: (value: any) => [
      <Field label="Device Type" input={<TextOutput />} value={value.DeviceType} key={0} horizontal />,
    ],
  },
  plate: {
    name: 'Plate',
    calibration: ({
      Mass: { value: massVal = undefined, uom: massUom = undefined } = {},
      Diameter: { value: diameterVal = undefined, uom: diameterUom = undefined } = {},
    }) => [
      <Field
        label={'Mass' + (massUom ? ` (${massUom})` : '')}
        input={<TextOutput />}
        value={massVal}
        format={{ nearest: 0.001 }}
        key={0}
        horizontal
      />,
      <Field
        label={'Diameter' + (diameterVal ? ` (${diameterUom})` : '')}
        input={<TextOutput />}
        value={diameterVal}
        format={{ nearest: 1 }}
        key={1}
        horizontal
      />,
    ],
  },
  'sedimentation-cylinder': {
    name: 'Sedimentation Cylinder',
  },
  sieve: {
    name: 'Sieve',
    endpoint: 'list.equipment.sieves',
    additionalDataFields: (value: any) => [
      <Field label="Sieve Size" input={<TextOutput />} value={value.Size?.Caption} key={0} horizontal />,
    ],
  },
  'sieve-set': {
    name: 'Sieve Set',
    additionalDataFields: ({
      Units: units,
      Dimensions: {
        Shape: shape = undefined,
        Diameter: diameter = undefined,
        Length: length = undefined,
        Width: width = undefined,
      } = {},
    }: any) => {
      // Dimensions are also configured in SieveSet/Config. Make sure changes to format are also made there.
      // TODO: Revise how equipment config is used so these aren't configured separately.
      switch (shape) {
        case SIEVE_SET_SHAPE_CIRCULAR:
          return [
            <Field
              label={'Diameter' + (!!diameter?.uom ? ` (${diameter?.uom})` : '')}
              input={<TextOutput />}
              value={diameter?.value}
              format={units === UNITS_METRIC ? { nearest: 1 } : { nearest: 0.1 }}
              key={0}
              horizontal
            />,
          ];
        case SIEVE_SET_SHAPE_RECTANGULAR:
          return [
            <Field
              label={'Length' + (!!length?.uom ? ` (${length?.uom})` : '')}
              input={<TextOutput />}
              value={length?.value}
              format={units === UNITS_METRIC ? { nearest: 1 } : { nearest: 0.1 }}
              key={0}
              horizontal
            />,
            <Field
              label={'Width' + (!!width?.uom ? ` (${width?.uom})` : '')}
              input={<TextOutput />}
              value={width?.value}
              format={units === UNITS_METRIC ? { nearest: 1 } : { nearest: 0.1 }}
              key={1}
              horizontal
            />,
          ];
        default:
          return [];
      }
    },
  },
  thermometer: {
    name: 'Thermometer',
    calibration: ({ Readings: readings = [] }: any) => {
      const unit = readings.length > 0 ? readings[0].Reading?.uom ?? readings[0].CorrectedTemperature?.uom : '';
      return [
        <Table
          key={0}
          colHeaders={['Reading ' + unit, 'Corrected Temperature ' + unit]}
          colWidths={200}
          data={readings.map((r: any) => [
            <TextOutput value={r.Reading?.value} format={{ nearest: 0.1 }} />,
            <TextOutput value={r.CorrectedTemperature?.value} format={{ nearest: 0.1 }} />,
          ])}
          alternateRowShading={true}
        />,
      ];
    },
  },
  timer: {
    name: 'Timer',
  },
  'moisture-tin': {
    name: 'Moisture Tin',
  },
};

export default EquipmentTypes;

/**
 * Lookup of equipment type keys by qest id
 *
 * Added because we need to be able to retrieve the key for the equipment type object above for a given equipment item
 * and this type is different than the type on the equipment model (it's not clear why)
 */
export const EquipmentTypeKeysByQestId: { [id: number]: string } = {
  30005: 'balance',
  30007: 'thermometer',
  30017: 'compression-machine',
};

export const EquipmentTypesByQestId: { [id: number]: IEquipmentType } = Object.fromEntries(
  Object.entries(EquipmentTypeKeysByQestId).map(([k, v]) => [k, EquipmentTypes[v]])
);
